import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ClipboardList,
  Plus,
  Camera,
  CheckCircle,
  AlertCircle,
  Home,
  Loader2,
  RefreshCw,
  Edit,
  Trash2,
  FileSignature,
  ArrowLeftRight,
  Scan,
  Box
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_OPTIONS = [
  { value: 'excellent', label: 'Excellent', color: 'bg-green-100 text-green-700' },
  { value: 'good', label: 'Good', color: 'bg-blue-100 text-blue-700' },
  { value: 'fair', label: 'Fair', color: 'bg-amber-100 text-amber-700' },
  { value: 'poor', label: 'Poor', color: 'bg-orange-100 text-orange-700' },
  { value: 'damaged', label: 'Damaged', color: 'bg-red-100 text-red-700' }
];

export default function InventoryManagement() {
  const { inventoryId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState(null);
  const [activeTab, setActiveTab] = useState('items');
  
  // Add item state
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    room: '',
    item_name: '',
    description: '',
    condition: 'good',
    quantity: 1,
    notes: ''
  });
  const [addingItem, setAddingItem] = useState(false);
  
  // Signature state
  const [signing, setSigning] = useState(false);
  
  // Comparison state
  const [comparisonResult, setComparisonResult] = useState(null);
  const [compareId, setCompareId] = useState('');
  const [comparing, setComparing] = useState(false);
  
  // Fetch inventory
  const fetchInventory = useCallback(async () => {
    if (!inventoryId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/inventory/${inventoryId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInventory(data);
      } else {
        toast.error('Failed to load inventory');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  }, [inventoryId, token]);
  
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);
  
  // Add item
  const handleAddItem = async () => {
    if (!newItem.room || !newItem.item_name) {
      toast.error('Please fill in room and item name');
      return;
    }
    
    setAddingItem(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/inventory/item`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inventory_id: inventoryId,
          ...newItem
        })
      });
      
      if (response.ok) {
        toast.success('Item added!');
        setNewItem({
          room: newItem.room, // Keep the room for multiple items
          item_name: '',
          description: '',
          condition: 'good',
          quantity: 1,
          notes: ''
        });
        fetchInventory();
      } else {
        toast.error('Failed to add item');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setAddingItem(false);
    }
  };
  
  // Update condition
  const handleUpdateCondition = async (itemId, condition, notes = null) => {
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/inventory/item/condition`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_id: itemId,
          condition,
          notes
        })
      });
      
      if (response.ok) {
        toast.success('Condition updated');
        fetchInventory();
      } else {
        toast.error('Failed to update');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };
  
  // Sign inventory
  const handleSign = async (signerType) => {
    setSigning(true);
    try {
      const response = await fetch(`${API_URL}/api/tenancy-lifecycle/inventory/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          inventory_id: inventoryId,
          signer_type: signerType
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Inventory signed as ${signerType}!`);
        fetchInventory();
        if (data.both_signed) {
          toast.success('Inventory completed - both parties have signed!');
        }
      } else {
        toast.error(data.detail || 'Failed to sign');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setSigning(false);
    }
  };
  
  // Compare inventories
  const handleCompare = async () => {
    if (!compareId) {
      toast.error('Please enter the check-out inventory ID');
      return;
    }
    
    setComparing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/tenancy-lifecycle/inventory/compare?check_in_id=${inventoryId}&check_out_id=${compareId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setComparisonResult(data);
        setActiveTab('compare');
      } else {
        toast.error('Failed to compare inventories');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setComparing(false);
    }
  };
  
  // Get unique rooms
  const rooms = inventory?.rooms ? Object.keys(inventory.rooms) : [];
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }
  
  if (!inventory) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Inventory Not Found</h2>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="inventory-management-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {inventory.type === 'check_in' ? 'Check-In' : inventory.type === 'check_out' ? 'Check-Out' : 'Interim'} Inventory
            </h1>
            <p className="text-gray-600">{inventory.property_address}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={inventory.status === 'completed' ? 'default' : 'secondary'}>
              {inventory.status}
            </Badge>
            <Button variant="outline" size="sm" onClick={fetchInventory}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4 text-center">
              <Box className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">{inventory.total_items || 0}</p>
              <p className="text-sm text-gray-500">Total Items</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <Home className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">{rooms.length}</p>
              <p className="text-sm text-gray-500">Rooms</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <FileSignature className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">
                {[inventory.signed_by_tenant, inventory.signed_by_landlord].filter(Boolean).length}/2
              </p>
              <p className="text-sm text-gray-500">Signatures</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <Scan className="w-8 h-8 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold">{inventory.has_lidar_scan ? 'Yes' : 'No'}</p>
              <p className="text-sm text-gray-500">LiDAR Scan</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="items">
              <ClipboardList className="w-4 h-4 mr-2" />
              Items by Room
            </TabsTrigger>
            <TabsTrigger value="add">
              <Plus className="w-4 h-4 mr-2" />
              Add Items
            </TabsTrigger>
            <TabsTrigger value="sign">
              <FileSignature className="w-4 h-4 mr-2" />
              Sign
            </TabsTrigger>
            <TabsTrigger value="compare">
              <ArrowLeftRight className="w-4 h-4 mr-2" />
              Compare
            </TabsTrigger>
          </TabsList>
          
          {/* Items Tab */}
          <TabsContent value="items" className="mt-6 space-y-6">
            {rooms.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Box className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-600">No Items Yet</h3>
                  <p className="text-sm text-gray-500 mt-2 mb-4">
                    Start adding items to the inventory
                  </p>
                  <Button onClick={() => setActiveTab('add')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Item
                  </Button>
                </CardContent>
              </Card>
            ) : (
              rooms.map((room) => (
                <Card key={room}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Home className="w-5 h-5" />
                      {room}
                    </CardTitle>
                    <CardDescription>
                      {inventory.rooms[room].length} items
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {inventory.rooms[room].map((item) => (
                        <div 
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          data-testid={`item-${item.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{item.name}</p>
                              {item.quantity > 1 && (
                                <Badge variant="outline">x{item.quantity}</Badge>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-500">{item.description}</p>
                            )}
                            {item.notes && (
                              <p className="text-sm text-amber-600 mt-1">{item.notes}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={item.condition || 'good'}
                              onChange={(e) => handleUpdateCondition(item.id, e.target.value)}
                              className={`text-sm border rounded px-2 py-1 ${
                                CONDITION_OPTIONS.find(c => c.value === item.condition)?.color || ''
                              }`}
                            >
                              {CONDITION_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
          
          {/* Add Items Tab */}
          <TabsContent value="add" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Add Inventory Item</CardTitle>
                <CardDescription>
                  Add items room by room. Photos can be added later.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Room *</Label>
                    <Input
                      value={newItem.room}
                      onChange={(e) => setNewItem(prev => ({ ...prev, room: e.target.value }))}
                      placeholder="e.g. Living Room, Kitchen, Bedroom 1"
                      list="rooms-list"
                      data-testid="room-input"
                    />
                    <datalist id="rooms-list">
                      {rooms.map(r => <option key={r} value={r} />)}
                      <option value="Living Room" />
                      <option value="Kitchen" />
                      <option value="Bedroom 1" />
                      <option value="Bedroom 2" />
                      <option value="Bathroom" />
                      <option value="Hallway" />
                      <option value="Garden" />
                    </datalist>
                  </div>
                  <div className="space-y-2">
                    <Label>Item Name *</Label>
                    <Input
                      value={newItem.item_name}
                      onChange={(e) => setNewItem(prev => ({ ...prev, item_name: e.target.value }))}
                      placeholder="e.g. Sofa, Dining Table, Fridge"
                      data-testid="item-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={newItem.description}
                    onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="e.g. Brown leather 3-seater sofa"
                    data-testid="description-input"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Condition</Label>
                    <select
                      value={newItem.condition}
                      onChange={(e) => setNewItem(prev => ({ ...prev, condition: e.target.value }))}
                      className="w-full border rounded-lg p-2"
                      data-testid="condition-select"
                    >
                      {CONDITION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newItem.quantity}
                      onChange={(e) => setNewItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      data-testid="quantity-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <textarea
                    value={newItem.notes}
                    onChange={(e) => setNewItem(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any additional notes about the item"
                    className="w-full border rounded-lg p-2 h-20"
                    data-testid="notes-input"
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button 
                  onClick={handleAddItem}
                  disabled={addingItem || !newItem.room || !newItem.item_name}
                  className="w-full"
                  data-testid="add-item-btn"
                >
                  {addingItem ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Item
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          {/* Sign Tab */}
          <TabsContent value="sign" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Sign Inventory</CardTitle>
                <CardDescription>
                  Both tenant and landlord must sign to complete the inventory
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Tenant Signature */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {inventory.signed_by_tenant ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <div className="w-8 h-8 rounded-full border-2 border-gray-300" />
                    )}
                    <div>
                      <p className="font-medium">Tenant Signature</p>
                      <p className="text-sm text-gray-500">
                        {inventory.signed_by_tenant 
                          ? `Signed on ${new Date(inventory.tenant_signature_at).toLocaleDateString()}`
                          : 'Not yet signed'
                        }
                      </p>
                    </div>
                  </div>
                  {!inventory.signed_by_tenant && user?.role === 'tenant' && (
                    <Button 
                      onClick={() => handleSign('tenant')}
                      disabled={signing}
                      data-testid="sign-tenant-btn"
                    >
                      {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign as Tenant'}
                    </Button>
                  )}
                </div>
                
                {/* Landlord Signature */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {inventory.signed_by_landlord ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <div className="w-8 h-8 rounded-full border-2 border-gray-300" />
                    )}
                    <div>
                      <p className="font-medium">Landlord Signature</p>
                      <p className="text-sm text-gray-500">
                        {inventory.signed_by_landlord 
                          ? `Signed on ${new Date(inventory.landlord_signature_at).toLocaleDateString()}`
                          : 'Not yet signed'
                        }
                      </p>
                    </div>
                  </div>
                  {!inventory.signed_by_landlord && ['landlord', 'landlord_admin', 'admin'].includes(user?.role) && (
                    <Button 
                      onClick={() => handleSign('landlord')}
                      disabled={signing}
                      data-testid="sign-landlord-btn"
                    >
                      {signing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign as Landlord'}
                    </Button>
                  )}
                </div>
                
                {inventory.signed_by_tenant && inventory.signed_by_landlord && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
                    <h3 className="font-semibold text-green-800">Inventory Completed</h3>
                    <p className="text-green-700">Both parties have signed this inventory.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Compare Tab */}
          <TabsContent value="compare" className="mt-6 space-y-6">
            {inventory.type === 'check_in' && !comparisonResult && (
              <Card>
                <CardHeader>
                  <CardTitle>Compare with Check-Out Inventory</CardTitle>
                  <CardDescription>
                    Compare this check-in inventory with a check-out inventory to identify any changes or damages.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Check-Out Inventory ID</Label>
                    <div className="flex gap-2">
                      <Input
                        value={compareId}
                        onChange={(e) => setCompareId(e.target.value)}
                        placeholder="Enter check-out inventory ID"
                        data-testid="compare-id-input"
                      />
                      <Button 
                        onClick={handleCompare}
                        disabled={comparing || !compareId}
                        data-testid="compare-btn"
                      >
                        {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Compare'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {comparisonResult && (
              <div className="space-y-6">
                {/* Summary */}
                <Card className={comparisonResult.has_issues ? 'border-amber-200' : 'border-green-200'}>
                  <CardContent className="py-6">
                    <div className="flex items-center gap-4">
                      {comparisonResult.has_issues ? (
                        <AlertCircle className="w-12 h-12 text-amber-500" />
                      ) : (
                        <CheckCircle className="w-12 h-12 text-green-500" />
                      )}
                      <div>
                        <h3 className="font-semibold text-lg">
                          {comparisonResult.has_issues ? 'Issues Found' : 'No Issues Found'}
                        </h3>
                        <p className="text-gray-600">
                          {comparisonResult.summary?.condition_changes || 0} condition changes, 
                          {' '}{comparisonResult.summary?.missing_count || 0} missing items,
                          {' '}{comparisonResult.summary?.damaged_count || 0} damaged items
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Condition Changes */}
                {comparisonResult.differences?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-amber-700">Condition Changes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {comparisonResult.differences.map((diff, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded">
                            <div>
                              <p className="font-medium">{diff.item}</p>
                              <p className="text-sm text-gray-500">{diff.room}</p>
                            </div>
                            <div className="text-right">
                              <Badge variant="outline">{diff.check_in_condition}</Badge>
                              <span className="mx-2">→</span>
                              <Badge className="bg-amber-100 text-amber-700">{diff.check_out_condition}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Missing Items */}
                {comparisonResult.missing_items?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-red-700">Missing Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {comparisonResult.missing_items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded">
                            <div>
                              <p className="font-medium">{item.item}</p>
                              <p className="text-sm text-gray-500">{item.room}</p>
                            </div>
                            <Badge className="bg-red-100 text-red-700">Missing</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Damaged Items */}
                {comparisonResult.damaged_items?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-red-700">Damaged Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {comparisonResult.damaged_items.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded">
                            <div>
                              <p className="font-medium">{item.item}</p>
                              <p className="text-sm text-gray-500">{item.room}</p>
                              {item.notes && <p className="text-sm text-red-600">{item.notes}</p>}
                            </div>
                            <Badge className="bg-red-100 text-red-700">Damaged</Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
